import React, { useState, useEffect, useCallback } from 'react';
import Card from './ui/Card';
import StatusBanner from './ui/StatusBanner';
import Button from './ui/Button';
import Modal, { ConfirmModal } from './ui/Modal';
import UsageBreakdownModal from './billing/UsageBreakdownModal';
import { Input, Select, Textarea } from './ui/FormField';
import {
  BanknotesIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ReceiptPercentIcon,
} from '@heroicons/react/24/outline';
import {
  adminBillingAPI,
  authAPI,
  AdminInvoice,
  InvoicePreview,
  BillingConfig,
  BillingPeriodSummary,
  InvoiceGenerationResult,
  BillingCredit,
  UsageDetail,
  triggerBlobDownload,
} from '../services/api';
import { apiErrorMessage } from '../utils/apiError';

type TabType = 'invoices' | 'config' | 'credits';

const AdminBilling: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('invoices');

  return (
    <div>
      <h1 className="text-3xl font-bold text-clinic-dark mb-6 font-body">Billing Management</h1>

      {/* Tabs - matching UserManagement pattern */}
      <div className="border-b border-clinic-primary-lighter mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('invoices')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'invoices'
                ? 'border-clinic-primary text-clinic-primary'
                : 'border-transparent text-clinic-muted hover:text-clinic-dark hover:border-clinic-primary-light'
            }`}
          >
            <DocumentTextIcon className="h-5 w-5 inline mr-2" />
            Invoices
          </button>
          <button
            onClick={() => setActiveTab('credits')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'credits'
                ? 'border-clinic-primary text-clinic-primary'
                : 'border-transparent text-clinic-muted hover:text-clinic-dark hover:border-clinic-primary-light'
            }`}
          >
            <ReceiptPercentIcon className="h-5 w-5 inline mr-2" />
            Credits
          </button>
          <button
            onClick={() => setActiveTab('config')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'config'
                ? 'border-clinic-primary text-clinic-primary'
                : 'border-transparent text-clinic-muted hover:text-clinic-dark hover:border-clinic-primary-light'
            }`}
          >
            <Cog6ToothIcon className="h-5 w-5 inline mr-2" />
            Configuration
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'invoices' && <InvoicesTab />}
      {activeTab === 'credits' && <CreditsTab />}
      {activeTab === 'config' && <ConfigTab />}
    </div>
  );
};

// =============================================================================
// INVOICES TAB
// =============================================================================

const InvoicesTab: React.FC = () => {
  const [invoices, setInvoices] = useState<AdminInvoice[]>([]);
  const [previews, setPreviews] = useState<InvoicePreview[]>([]);
  const [summary, setSummary] = useState<BillingPeriodSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [billingPeriod, setBillingPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);

  // Per-booking usage detail (the lines behind a preview / invoice total)
  const [usageOpen, setUsageOpen] = useState(false);
  const [usageTarget, setUsageTarget] = useState<{ clinicianId: number; period: string } | null>(null);
  const [usageDetail, setUsageDetail] = useState<UsageDetail | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState<string | null>(null);

  const openUsageDetail = async (clinicianId: number, period: string) => {
    setUsageTarget({ clinicianId, period });
    setUsageOpen(true);
    setUsageDetail(null);
    setUsageError(null);
    setUsageLoading(true);
    try {
      setUsageDetail(await adminBillingAPI.getUsageDetail(clinicianId, period));
    } catch (err: any) {
      setUsageError(apiErrorMessage(err, 'Failed to load usage detail'));
    } finally {
      setUsageLoading(false);
    }
  };

  const downloadUsageCsv = async (period: string, clinicianId?: number, label?: string) => {
    try {
      const blob = await adminBillingAPI.downloadUsageCsv(period, clinicianId);
      const suffix = label ? `-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}` : '';
      triggerBlobDownload(blob, `usage-${period}${suffix}.csv`);
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Failed to download usage CSV'));
    }
  };

  const fetchInvoices = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [invoiceData, summaryData] = await Promise.all([
        adminBillingAPI.getInvoices(billingPeriod || undefined, statusFilter || undefined),
        billingPeriod ? adminBillingAPI.getSummary(billingPeriod) : Promise.resolve(null),
      ]);
      setInvoices(invoiceData);
      setSummary(summaryData);
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Failed to fetch invoices'));
    } finally {
      setIsLoading(false);
    }
  }, [billingPeriod, statusFilter]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handlePreview = async () => {
    if (!billingPeriod) return;
    try {
      setIsPreviewing(true);
      setError(null);
      const data = await adminBillingAPI.previewAllInvoices(billingPeriod);
      setPreviews(data);
      setShowPreview(true);
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Failed to generate preview'));
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleGenerate = async () => {
    if (!billingPeriod) return;
    if (!window.confirm(`Generate invoices for ${billingPeriod}? This will create invoice records for all active members.`)) {
      return;
    }
    try {
      setIsGenerating(true);
      setError(null);
      setSuccessMessage(null);
      const result: InvoiceGenerationResult = await adminBillingAPI.generateInvoices(billingPeriod, true);
      setSuccessMessage(
        `Generated ${result.invoices_created} invoice(s). ` +
        `Skipped: ${result.invoices_skipped}. Existing: ${result.invoices_existing}.`
      );
      setShowPreview(false);
      fetchInvoices();
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Failed to generate invoices'));
    } finally {
      setIsGenerating(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-clinic-muted/20 text-clinic-muted',
      open: 'bg-clinic-primary/20 text-clinic-primary',
      paid: 'bg-green-100 text-green-700',
      failed: 'bg-clinic-accent/20 text-clinic-accent',
      void: 'bg-gray-100 text-gray-500',
    };
    const icons: Record<string, React.ReactNode> = {
      pending: <ClockIcon className="h-4 w-4 mr-1" />,
      open: <ArrowPathIcon className="h-4 w-4 mr-1" />,
      paid: <CheckCircleIcon className="h-4 w-4 mr-1" />,
      failed: <XCircleIcon className="h-4 w-4 mr-1" />,
      void: <XCircleIcon className="h-4 w-4 mr-1" />,
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Status Messages */}
      {error && (
        <StatusBanner
          variant="error"
          title="Error"
          message={error}
          dismissible
          onDismiss={() => setError(null)}
        />
      )}
      {successMessage && (
        <StatusBanner
          variant="success"
          title="Success"
          message={successMessage}
          dismissible
          onDismiss={() => setSuccessMessage(null)}
        />
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-clinic-primary/20 rounded-full flex items-center justify-center">
                <BanknotesIcon className="h-5 w-5 text-clinic-primary" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-clinic-muted">Total Revenue</p>
                <p className="text-2xl font-semibold text-clinic-dark">${summary.total_revenue.toFixed(2)}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircleIcon className="h-5 w-5 text-green-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-clinic-muted">Paid</p>
                <p className="text-2xl font-semibold text-clinic-dark">{summary.paid_count}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-clinic-primary/20 rounded-full flex items-center justify-center">
                <ClockIcon className="h-5 w-5 text-clinic-primary" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-clinic-muted">Pending/Open</p>
                <p className="text-2xl font-semibold text-clinic-dark">{summary.pending_count + summary.open_count}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-clinic-accent/20 rounded-full flex items-center justify-center">
                <ExclamationTriangleIcon className="h-5 w-5 text-clinic-accent" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-clinic-muted">Failed</p>
                <p className="text-2xl font-semibold text-clinic-dark">{summary.failed_count}</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Filters and Actions */}
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-clinic-muted mb-1">Billing Period</label>
            <input
              type="month"
              value={billingPeriod}
              onChange={(e) => setBillingPeriod(e.target.value)}
              className="px-3 py-2 border border-clinic-primary-lighter rounded-lg focus:outline-none focus:ring-2 focus:ring-clinic-primary focus:border-transparent text-clinic-dark"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-clinic-muted mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-clinic-primary-lighter rounded-lg focus:outline-none focus:ring-2 focus:ring-clinic-primary focus:border-transparent text-clinic-dark"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="open">Open</option>
              <option value="paid">Paid</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div className="flex-1" />
          <button
            onClick={handlePreview}
            disabled={isPreviewing || !billingPeriod}
            className="inline-flex items-center px-4 py-2 border border-clinic-primary text-clinic-primary rounded-lg hover:bg-clinic-cream transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            <EyeIcon className="h-5 w-5 mr-2" />
            {isPreviewing ? 'Loading...' : 'Preview'}
          </button>
          <button
            onClick={handleGenerate}
            disabled={isGenerating || !billingPeriod}
            className="inline-flex items-center px-4 py-2 bg-clinic-primary text-white rounded-lg hover:bg-clinic-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-clinic-sm"
          >
            <BanknotesIcon className="h-5 w-5 mr-2" />
            {isGenerating ? 'Generating...' : 'Generate Invoices'}
          </button>
          <button
            onClick={async () => {
              if (!billingPeriod) return;
              try {
                const blob = await adminBillingAPI.bulkDownloadInvoices(billingPeriod);
                const { triggerBlobDownload } = await import('../services/api');
                triggerBlobDownload(blob, `invoices-${billingPeriod}.zip`);
              } catch (e) {
                setError('Failed to download invoices');
              }
            }}
            disabled={!billingPeriod || invoices.length === 0}
            className="inline-flex items-center px-4 py-2 border border-clinic-primary text-clinic-primary rounded-lg hover:bg-clinic-cream transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            Download All
          </button>
          <button
            onClick={() => billingPeriod && downloadUsageCsv(billingPeriod)}
            disabled={!billingPeriod}
            className="inline-flex items-center px-4 py-2 border border-clinic-primary text-clinic-primary rounded-lg hover:bg-clinic-cream transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            <DocumentTextIcon className="h-5 w-5 mr-2" />
            Usage CSV
          </button>
        </div>
      </Card>

      {/* Preview Section */}
      {showPreview && previews.length > 0 && (
        <Card padding="none" className="border-2 border-clinic-gold">
          <div className="px-6 py-4 border-b border-clinic-primary-lighter flex items-center justify-between bg-clinic-gold/10">
            <h2 className="text-lg font-semibold text-clinic-dark">
              Invoice Preview - {billingPeriod}
            </h2>
            <button
              onClick={() => setShowPreview(false)}
              className="text-clinic-muted hover:text-clinic-dark transition-colors"
            >
              <XCircleIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-clinic-primary-lighter">
              <thead className="bg-clinic-cream/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">Clinician</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">Studio</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">Pod</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">Membership</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">Usage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">Credit</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">Total</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-clinic-primary-lighter bg-white">
                {previews.map((preview) => (
                  <tr key={preview.clinician_id} className="hover:bg-clinic-cream/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-medium text-clinic-dark">{preview.clinician_name}</div>
                      <div className="text-sm text-clinic-muted">{preview.clinician_email}</div>
                    </td>
                    <td className="px-6 py-4 text-clinic-dark capitalize">{preview.user_type}</td>
                    <td className="px-6 py-4 text-clinic-dark">{(preview.total_hours - (preview.pod_hours || 0)).toFixed(2)}h</td>
                    <td className="px-6 py-4 text-clinic-dark">
                      {(preview.pod_hours || 0) > 0 ? (
                        <>
                          {preview.pod_hours.toFixed(2)}h
                          <span className="block text-xs text-clinic-muted">${preview.pod_amount.toFixed(2)}</span>
                        </>
                      ) : '—'}
                    </td>
                    <td className="px-6 py-4 text-clinic-dark">${preview.membership_fee.toFixed(2)}</td>
                    <td className="px-6 py-4 text-clinic-dark">${(preview.subtotal - preview.membership_fee).toFixed(2)}</td>
                    <td className="px-6 py-4 text-clinic-accent">{preview.credit_amount > 0 ? `-$${preview.credit_amount.toFixed(2)}` : '—'}</td>
                    <td className="px-6 py-4 font-semibold text-clinic-dark">${preview.total_amount.toFixed(2)}</td>
                    <td className="px-6 py-4">
                      <button
                        type="button"
                        onClick={() => openUsageDetail(preview.clinician_id, preview.billing_period)}
                        className="text-clinic-primary hover:text-clinic-primary-dark text-sm font-medium whitespace-nowrap"
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-clinic-cream/70 font-semibold">
                  <td className="px-6 py-4 text-clinic-dark" colSpan={7}>Total ({previews.length} clinicians)</td>
                  <td className="px-6 py-4 text-clinic-dark">${previews.reduce((sum, p) => sum + p.total_amount, 0).toFixed(2)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="px-6 py-4 border-t border-clinic-primary-lighter bg-clinic-cream/30 flex justify-end">
            <button
              onClick={handleGenerate}
              disabled={isGenerating}
              className="inline-flex items-center px-4 py-2 bg-clinic-primary text-white rounded-lg hover:bg-clinic-primary/90 transition-colors disabled:opacity-50 font-medium shadow-clinic-sm"
            >
              <BanknotesIcon className="h-5 w-5 mr-2" />
              {isGenerating ? 'Generating...' : 'Confirm & Generate'}
            </button>
          </div>
        </Card>
      )}

      {/* Invoice List */}
      <Card padding="none" className="overflow-hidden">
        <div className="px-6 py-4 border-b border-clinic-primary-lighter">
          <h2 className="text-lg font-semibold text-clinic-dark">
            Invoices {invoices.length > 0 && `(${invoices.length})`}
          </h2>
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-clinic-primary"></div>
          </div>
        ) : invoices.length === 0 ? (
          <div className="p-8 text-center">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-clinic-muted mb-4" />
            <h3 className="text-lg font-medium text-clinic-dark mb-2">No invoices found</h3>
            <p className="text-clinic-muted">No invoices found for the selected period.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-clinic-primary-lighter">
              <thead className="bg-clinic-cream/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">Clinician</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">Period</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">Hours</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">Credit</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-clinic-primary-lighter bg-white">
                {invoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-clinic-cream/30 transition-colors">
                    <td className="px-6 py-4 text-sm text-clinic-muted">{invoice.id}</td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-clinic-dark">{invoice.clinician_name}</div>
                      <div className="text-sm text-clinic-muted">{invoice.clinician_email}</div>
                    </td>
                    <td className="px-6 py-4 text-clinic-dark">{invoice.billing_period}</td>
                    <td className="px-6 py-4 text-clinic-dark capitalize">{invoice.user_type}</td>
                    <td className="px-6 py-4 text-clinic-dark">{invoice.total_hours.toFixed(2)}h</td>
                    <td className="px-6 py-4 text-clinic-accent">{invoice.credit_amount > 0 ? `-$${invoice.credit_amount.toFixed(2)}` : '—'}</td>
                    <td className="px-6 py-4 font-semibold text-clinic-dark">${invoice.total_amount.toFixed(2)}</td>
                    <td className="px-6 py-4">{getStatusBadge(invoice.status)}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => openUsageDetail(invoice.clinician_id, invoice.billing_period)}
                          className="text-clinic-primary hover:text-clinic-primary-dark text-sm font-medium"
                        >
                          Details
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              const blob = await adminBillingAPI.downloadInvoicePdf(invoice.id);
                              const { triggerBlobDownload } = await import('../services/api');
                              triggerBlobDownload(blob, `INV-${invoice.billing_period}-${String(invoice.id).padStart(4, '0')}.pdf`);
                            } catch (e) {
                              setError('Failed to download PDF');
                            }
                          }}
                          className="text-clinic-primary hover:text-clinic-primary-dark text-sm font-medium"
                        >
                          PDF
                        </button>
                        {invoice.status !== 'paid' && invoice.status !== 'void' && (
                          <button
                            onClick={async () => {
                              try {
                                await adminBillingAPI.markInvoicePaid(invoice.id);
                                fetchInvoices();
                              } catch (e) {
                                setError('Failed to mark as paid');
                              }
                            }}
                            className="text-green-600 hover:text-green-800 text-sm font-medium"
                          >
                            Mark Paid
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <UsageBreakdownModal
        isOpen={usageOpen}
        onClose={() => setUsageOpen(false)}
        detail={usageDetail}
        loading={usageLoading}
        error={usageError}
        onDownloadCsv={usageTarget ? () => downloadUsageCsv(usageTarget.period, usageTarget.clinicianId, usageDetail?.clinician_name) : undefined}
      />
    </div>
  );
};

// =============================================================================
// CONFIG TAB
// =============================================================================

const ConfigTab: React.FC = () => {
  const [config, setConfig] = useState<BillingConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setIsLoading(true);
      const data = await adminBillingAPI.getConfig();
      setConfig(data);
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Failed to fetch configuration'));
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-clinic-primary"></div>
      </div>
    );
  }

  if (error) {
    return <StatusBanner variant="error" title="Error" message={error} />;
  }

  if (!config) {
    return <StatusBanner variant="error" title="Error" message="Configuration not available" />;
  }

  // Group time slots by day
  const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const slotsByDay = dayOrder.reduce((acc, day) => {
    acc[day] = config.time_slots.filter(s => s.day_of_week === day).sort((a, b) => a.start_time.localeCompare(b.start_time));
    return acc;
  }, {} as Record<string, typeof config.time_slots>);

  // Group rates by user type
  const ratesByUserType = config.rates.reduce((acc, rate) => {
    if (!acc[rate.user_type]) acc[rate.user_type] = {};
    acc[rate.user_type][rate.time_category] = rate.hourly_rate;
    return acc;
  }, {} as Record<string, Record<string, number>>);

  const getCategoryStyle = (category: string) => {
    switch (category) {
      case 'peak': return 'bg-clinic-accent/20 text-clinic-accent border-clinic-accent/30';
      case 'standard': return 'bg-clinic-gold/20 text-clinic-gold border-clinic-gold/30';
      case 'off_peak': return 'bg-clinic-primary/20 text-clinic-primary border-clinic-primary/30';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      <StatusBanner
        variant="info"
        title="Read-Only Configuration"
        message="Configuration is read-only for MVP. Contact support to modify billing rates."
      />

      {/* Membership Fee */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-clinic-dark mb-4">Membership Fee</h3>
        {config.membership_fee ? (
          <div>
            <p className="text-3xl font-bold text-clinic-primary">${config.membership_fee.amount.toFixed(2)}</p>
            <p className="text-sm text-clinic-muted mt-1">Monthly flat fee for all clinicians</p>
          </div>
        ) : (
          <p className="text-clinic-muted">No membership fee configured</p>
        )}
      </Card>

      {/* User Type Thresholds */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-clinic-dark mb-2">User Type Thresholds</h3>
        <p className="text-sm text-clinic-muted mb-4">Clinicians are categorized by their monthly usage hours</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {config.thresholds.map((threshold) => {
            // Boundaries are stored as the next tier's first value (e.g. 50.01 to
            // mean "anything over 50"), so floor the .01 tail back to the human
            // number for display: light => "Up to 30", medium => "Over 30 to 50",
            // heavy => "Over 50".
            const human = (v: number) => (Number.isInteger(v) ? v : Math.floor(v));
            const range =
              threshold.max_hours == null
                ? `Over ${human(threshold.min_hours)} hours/month`
                : threshold.min_hours <= 0
                ? `Up to ${human(threshold.max_hours)} hours/month`
                : `Over ${human(threshold.min_hours)} to ${human(threshold.max_hours)} hours/month`;
            return (
              <div
                key={threshold.id}
                className="p-4 border border-clinic-primary-lighter rounded-lg bg-white"
              >
                <h4 className="font-semibold text-clinic-dark capitalize text-lg">{threshold.user_type}</h4>
                <p className="text-clinic-muted">{range}</p>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Billing Rates */}
      <Card padding="none" className="overflow-hidden">
        <div className="px-6 py-4 border-b border-clinic-primary-lighter">
          <h3 className="text-lg font-semibold text-clinic-dark">Hourly Rates by User Type</h3>
          <p className="text-sm text-clinic-muted">Rates applied based on user type and time category</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-clinic-primary-lighter">
            <thead className="bg-clinic-cream/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">User Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">Peak</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">Standard</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-clinic-muted uppercase tracking-wider">Off-Peak</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-clinic-primary-lighter bg-white">
              {['light', 'medium', 'heavy'].map((userType) => (
                <tr key={userType} className="hover:bg-clinic-cream/30 transition-colors">
                  <td className="px-6 py-4 font-medium text-clinic-dark capitalize">{userType}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium border ${getCategoryStyle('peak')}`}>
                      ${ratesByUserType[userType]?.peak?.toFixed(2) || '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium border ${getCategoryStyle('standard')}`}>
                      ${ratesByUserType[userType]?.standard?.toFixed(2) || '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium border ${getCategoryStyle('off_peak')}`}>
                      ${ratesByUserType[userType]?.off_peak?.toFixed(2) || '-'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Time Slot Categories */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-clinic-dark mb-2">Time Slot Categories</h3>
        <p className="text-sm text-clinic-muted mb-4">When each rate category applies throughout the week</p>
        <div className="space-y-3">
          {dayOrder.map((day) => (
            <div key={day} className="flex items-center">
              <div className="w-28 font-medium text-clinic-dark capitalize">{day}</div>
              <div className="flex-1 flex flex-wrap gap-2">
                {slotsByDay[day]?.map((slot) => (
                  <span
                    key={slot.id}
                    className={`px-3 py-1 text-xs rounded-full font-medium border ${getCategoryStyle(slot.category)}`}
                    title={`${slot.category}: ${slot.start_time} - ${slot.end_time}`}
                  >
                    {slot.start_time}-{slot.end_time}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-6 pt-4 border-t border-clinic-primary-lighter flex gap-4 text-sm">
          <span className={`px-3 py-1 rounded-full font-medium border ${getCategoryStyle('peak')}`}>Peak</span>
          <span className={`px-3 py-1 rounded-full font-medium border ${getCategoryStyle('standard')}`}>Standard</span>
          <span className={`px-3 py-1 rounded-full font-medium border ${getCategoryStyle('off_peak')}`}>Off-Peak</span>
        </div>
      </Card>
    </div>
  );
};

// =============================================================================
// CREDITS TAB (Module B)
// =============================================================================

interface MemberOption { id: number; name: string; }

const currentPeriod = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// Warn if we're within 24h of month-end (the invoice run), where a new credit
// for the current period might miss / collide with generation.
const nearMonthEnd = (period: string): boolean => {
  if (period !== currentPeriod()) return false;
  const now = new Date();
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  return now.getDate() >= lastDay; // last calendar day of the current month
};

const CreditsTab: React.FC = () => {
  const [credits, setCredits] = useState<BillingCredit[]>([]);
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [filterClinician, setFilterClinician] = useState('');
  const [filterApplied, setFilterApplied] = useState<'' | 'true' | 'false'>('');

  // Add dialog
  const [showAdd, setShowAdd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formClinician, setFormClinician] = useState('');
  const [formAmount, setFormAmount] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formPeriod, setFormPeriod] = useState(currentPeriod());
  const [formError, setFormError] = useState<string | null>(null);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<BillingCredit | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchCredits = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminBillingAPI.getCredits({
        clinician_id: filterClinician ? Number(filterClinician) : undefined,
        applied: filterApplied === '' ? undefined : filterApplied === 'true',
      });
      setCredits(data);
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Failed to load credits'));
    } finally {
      setLoading(false);
    }
  }, [filterClinician, filterApplied]);

  useEffect(() => { fetchCredits(); }, [fetchCredits]);

  useEffect(() => {
    authAPI.getAllUsers()
      .then((users) => setMembers(
        users.filter((u: any) => u.role === 'member').map((u: any) => ({ id: u.id, name: u.full_name || u.email }))
      ))
      .catch(() => { /* non-fatal: dialog just has no options */ });
  }, []);

  const resetForm = () => {
    setFormClinician(''); setFormAmount(''); setFormDescription('');
    setFormPeriod(currentPeriod()); setFormError(null);
  };

  const submitCredit = async () => {
    if (!formClinician) return setFormError('Select a clinician');
    if (!(Number(formAmount) > 0)) return setFormError('Amount must be greater than 0');
    if (!formDescription.trim()) return setFormError('Description is required');
    if (!/^\d{4}-\d{2}$/.test(formPeriod)) return setFormError('Period must be YYYY-MM');
    try {
      setSubmitting(true);
      setFormError(null);
      await adminBillingAPI.createCredit({
        clinician_id: Number(formClinician),
        amount: Number(formAmount).toFixed(2),
        description: formDescription.trim(),
        target_billing_period: formPeriod,
      });
      setShowAdd(false);
      resetForm();
      fetchCredits();
    } catch (err: any) {
      setFormError(apiErrorMessage(err, 'Failed to create credit'));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await adminBillingAPI.deleteCredit(deleteTarget.id);
      setDeleteTarget(null);
      fetchCredits();
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Failed to void credit'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      {error && <div className="mb-4"><StatusBanner variant="error" title="Error" message={error} dismissible onDismiss={() => setError(null)} /></div>}

      {/* Filter + add bar */}
      <Card className="p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-clinic-muted mb-1">Clinician</label>
            <select value={filterClinician} onChange={(e) => setFilterClinician(e.target.value)}
              className="text-sm border border-clinic-primary-lighter rounded-input px-2 py-1.5 bg-white">
              <option value="">All</option>
              {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-clinic-muted mb-1">Status</label>
            <select value={filterApplied} onChange={(e) => setFilterApplied(e.target.value as any)}
              className="text-sm border border-clinic-primary-lighter rounded-input px-2 py-1.5 bg-white">
              <option value="">All</option>
              <option value="false">Pending</option>
              <option value="true">Applied</option>
            </select>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchCredits} leftIcon={<ArrowPathIcon className="h-4 w-4" />}>Refresh</Button>
          <Button variant="primary" size="sm" className="ml-auto" onClick={() => { resetForm(); setShowAdd(true); }}>
            Add credit
          </Button>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-clinic-primary" />
          </div>
        ) : credits.length === 0 ? (
          <div className="text-center py-12">
            <ReceiptPercentIcon className="h-12 w-12 text-clinic-muted mx-auto mb-3" />
            <p className="text-clinic-dark font-medium">No credits</p>
            <p className="text-sm text-clinic-muted mt-1">Add a credit to reduce a clinician's next invoice</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-clinic-surface-sunken text-clinic-muted text-xs uppercase tracking-wide">
                  <th className="text-left font-medium px-4 py-3">Clinician</th>
                  <th className="text-right font-medium px-4 py-3">Amount</th>
                  <th className="text-left font-medium px-4 py-3">Description</th>
                  <th className="text-left font-medium px-4 py-3">Period</th>
                  <th className="text-left font-medium px-4 py-3">Status</th>
                  <th className="text-right font-medium px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {credits.map((c) => (
                  <tr key={c.id} className="border-t border-clinic-primary-lighter hover:bg-clinic-surface/60 transition-colors">
                    <td className="px-4 py-3 text-clinic-dark">{c.clinician_name}</td>
                    <td className="px-4 py-3 text-right text-clinic-dark font-medium">${Number(c.amount).toFixed(2)}</td>
                    <td className="px-4 py-3 text-clinic-muted">{c.description}</td>
                    <td className="px-4 py-3 text-clinic-muted">{c.target_billing_period}</td>
                    <td className="px-4 py-3">
                      {c.applied_at ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-clinic-primary/15 text-clinic-primary-dark">
                          Applied{c.applied_to_invoice_id ? ` · #${c.applied_to_invoice_id}` : ''}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-clinic-gold/20 text-clinic-brown">Pending</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {c.applied_at ? (
                        <span className="text-xs text-clinic-muted">—</span>
                      ) : (
                        <Button variant="danger" size="sm" onClick={() => setDeleteTarget(c)}>Void</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Add credit dialog */}
      <Modal isOpen={showAdd} onClose={() => { if (!submitting) setShowAdd(false); }} title="Add billing credit" size="md">
        <div className="space-y-4">
          {formError && <StatusBanner variant="error" title="Couldn't save" message={formError} />}
          <div>
            <label className="block text-sm font-medium text-clinic-dark mb-1.5">Clinician</label>
            <Select value={formClinician} onChange={(e) => setFormClinician(e.target.value)}
              placeholder="Select a clinician…"
              options={members.map((m) => ({ value: String(m.id), label: m.name }))} />
          </div>
          <div>
            <label className="block text-sm font-medium text-clinic-dark mb-1.5">Amount (CAD)</label>
            <Input type="number" min="0.01" step="0.01" value={formAmount}
              onChange={(e) => setFormAmount(e.target.value)} placeholder="0.00" />
          </div>
          <div>
            <label className="block text-sm font-medium text-clinic-dark mb-1.5">Description</label>
            <Textarea value={formDescription} onChange={(e) => setFormDescription(e.target.value)} rows={2}
              placeholder="Why is this credit being applied?" />
          </div>
          <div>
            <label className="block text-sm font-medium text-clinic-dark mb-1.5">Target billing period</label>
            <Input type="month" value={formPeriod} onChange={(e) => setFormPeriod(e.target.value)} />
            <p className="text-xs text-clinic-muted mt-1">Applied (pre-tax) when this period's invoice is generated.</p>
            {nearMonthEnd(formPeriod) && (
              <p className="text-xs text-clinic-accent mt-1">⚠ This period's invoice run is imminent — add the credit before generation.</p>
            )}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => { if (!submitting) setShowAdd(false); }} disabled={submitting}>Cancel</Button>
            <Button variant="primary" onClick={submitCredit} isLoading={submitting} disabled={submitting}>Add credit</Button>
          </div>
        </div>
      </Modal>

      {/* Void confirmation */}
      <ConfirmModal
        isOpen={deleteTarget !== null}
        onClose={() => { if (!deleting) setDeleteTarget(null); }}
        onConfirm={confirmDelete}
        title="Void this credit?"
        message={`Void the $${deleteTarget ? Number(deleteTarget.amount).toFixed(2) : ''} credit for ${deleteTarget?.clinician_name ?? ''}? Only unapplied credits can be voided.`}
        confirmText="Void credit"
        variant="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default AdminBilling;
